"use client";

import { useSearchParams } from "next/navigation";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import type { ComponentProps } from "react";

import { Tag } from "@/components/typography/Tag";
import { fetchStoryblokList } from "@/lib/storyblok/fetchList";
import type { CategoryStoryblok } from "@/component-types-sb";
import { cn } from "@/lib/utils";

type CategoriesProps = { label?: string } & ComponentProps<"div">;

const Categories = ({ label, ...rest }: CategoriesProps) => {
  const [categories, setCategories] = useState<CategoryStoryblok[] | null>([]);

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const query = searchParams.get("category")?.toLocaleLowerCase() || undefined;

  // Ueber den gecachten Proxy statt ueber die "use server"-Action: die lief
  // pro Seitenaufruf gegen Storyblok, der Proxy teilt einen Cache-Eintrag
  // unter allen Besuchern. `per_page: 100` verhindert die 25er-Pagination
  // des Clients (es gibt nur eine Handvoll Kategorien).
  useEffect(() => {
    const controller = new AbortController();

    fetchStoryblokList<CategoryStoryblok>(
      { content_type: "category", per_page: 100 },
      controller.signal,
    )
      .then(setCategories)
      .catch((err) => {
        if (err?.name !== "AbortError") console.error(err);
      });

    return () => controller.abort();
  }, []);

  return (
    <div
      className={cn(
        "flex items-center gap-2 flex-wrap",
        rest.className
      )}
    >
      <div>{label}</div>
      <Tag label="Alle" href={pathname} active={!query} />
      {categories?.map((category) => (
        <Tag
          key={category.id}
          label={category.content?.name || ""}
          href={
            query === category.name?.toLocaleLowerCase()
              ? pathname
              : `?category=${category.name?.toLocaleLowerCase()}`
          }
          active={query === category.name?.toLocaleLowerCase()}
        />
      ))}
    </div>
  );
};

export { Categories };
